import { useEffect, useId, useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEventHandler, type ReactNode } from "react";
import { FiArrowDownRight, FiArrowUpRight, FiCheck, FiChevronRight, FiCopy, FiCreditCard, FiDollarSign, FiEdit2, FiMoreHorizontal, FiRepeat, FiTrash2 } from "react-icons/fi";
import { createPortal } from "react-dom";
import { LoadingSpinner } from "./primitives";

export type EntityCardTone = "positive" | "negative" | "warning" | "neutral";

export type EntityCardAction = {
  kind: "duplicate" | "edit" | "delete";
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
};

export type EntityCardProgress = {
  percent: number;
  /** Semantic tone, same vocabulary as `valueTone`. Never a raw CSS color. */
  tone?: EntityCardTone;
  /** Defaults to the clamped percent; pass it only when the real figure exceeds 100%. */
  label?: ReactNode;
  ariaLabel?: string;
  valueText?: string;
};

type EntityCardProps = {
  title: ReactNode;
  /** Colored dot before the title (budget), tinted from the shared tone set. */
  titleDot?: EntityCardTone;
  /** Free node before the title (emoji, flag) — savings goals. */
  titleLead?: ReactNode;
  subtitle?: ReactNode;
  description?: ReactNode;
  /** Placeholder for rows with no description; rendered muted, never as real content. */
  descriptionFallback?: ReactNode;
  /** Bottom chip row: account, category, tag, ticket link… */
  meta?: ReactNode[];
  value?: ReactNode;
  valueTone?: EntityCardTone;
  valueCaption?: ReactNode;
  progress?: EntityCardProgress;
  actions?: EntityCardAction[];
  isSwiped?: boolean;
  onToggle?: (event: MouseEvent<HTMLSpanElement>) => void;
  toggleLabel?: string;
  /** Multi-select mode (transactions): hides actions, shows a checkbox. */
  selection?: { active: boolean; selected: boolean };
  onClick: () => void;
  onPointerDown?: PointerEventHandler<HTMLButtonElement>;
  onPointerMove?: PointerEventHandler<HTMLButtonElement>;
  onPointerUp?: PointerEventHandler<HTMLButtonElement>;
  onPointerLeave?: PointerEventHandler<HTMLButtonElement>;
  onPointerCancel?: PointerEventHandler<HTMLButtonElement>;
  onContextMenu?: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Account rows use the catalog's aligned inflow/outflow columns and mobile summary. */
  accountRow?: {
    accessibleLabel: string;
    accountTypeLabel: ReactNode;
    incomeLabel: ReactNode;
    income: ReactNode;
    expenseLabel: ReactNode;
    expense: ReactNode;
    iconTone: "lilac" | "mist" | "sun";
  };
};

const TONE_CLASS: Record<EntityCardTone, string> = {
  positive: "text-[var(--app-success)]",
  negative: "text-[var(--app-danger)]",
  warning: "text-[var(--app-warning)]",
  neutral: "text-[var(--app-text)]",
};

/** One fill per tone, one track for every bar in the app. */
const PROGRESS_TONE: Record<EntityCardTone, string> = {
  positive: "var(--app-success)",
  negative: "var(--app-danger)",
  warning: "var(--app-warning)",
  neutral: "var(--app-primary)",
};


const ACTION_ICON = {
  duplicate: FiCopy,
  edit: FiEdit2,
  delete: FiTrash2,
} as const;

const ACTION_CLASS = {
  duplicate:
    "bg-[color:color-mix(in_srgb,var(--app-success)_14%,var(--app-surface)_86%)] text-[color:color-mix(in_srgb,var(--app-success)_74%,var(--app-text)_26%)]",
  edit: "bg-[color:color-mix(in_srgb,var(--app-surface-soft)_92%,white)] text-[var(--app-text)]",
  delete: "bg-[var(--app-danger-soft)] text-[var(--app-danger)]",
} as const;

/** Width of a single swipe action button, in rem. Keep in sync with `w-20`. */
const ACTION_WIDTH_REM = 5;

export const EntityCard = (props: EntityCardProps) =>
  props.accountRow ? <AccountEntityCard {...props} accountRow={props.accountRow} /> : <StandardEntityCard {...props} />;

const AccountEntityCard = ({
  title,
  description,
  value,
  valueTone = "neutral",
  progress,
  actions = [],
  isSwiped = false,
  toggleLabel,
  onClick,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerLeave,
  onPointerCancel,
  accountRow,
}: EntityCardProps & { accountRow: NonNullable<EntityCardProps["accountRow"]> }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const offset = isSwiped ? actions.length * ACTION_WIDTH_REM : 0;
  const BalanceIcon = valueTone === "negative" ? FiArrowDownRight : valueTone === "positive" ? FiArrowUpRight : valueTone === "warning" ? FiRepeat : null;
  const Icon = accountRow.iconTone === "sun" ? FiDollarSign : FiCreditCard;

  useEffect(() => {
    if (!menuOpen) return;
    const firstItem = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)');
    if (firstItem) firstItem.focus();
    else menuRef.current?.focus();
    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const menuHeight = actions.length * 44 + 16;
      const top = rect.bottom + menuHeight + 8 > window.innerHeight ? rect.top - menuHeight - 8 : rect.bottom + 8;
      setMenuPosition({ top, left: Math.max(8, Math.min(window.innerWidth - 208, rect.right - 200)) });
    };
    const closeOutside = (event: PointerEvent) => {
      if (!rowRef.current?.contains(event.target as Node) && !menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    document.addEventListener("scroll", updatePosition, true);
    document.addEventListener("pointerdown", closeOutside);
    return () => {
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("scroll", updatePosition, true);
      document.removeEventListener("pointerdown", closeOutside);
    };
  }, [actions.length, menuOpen]);

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? []);
    const currentIndex = items.indexOf(document.activeElement as HTMLElement);
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setMenuOpen(false);
      triggerRef.current?.focus();
    } else if ((event.key === "ArrowDown" || event.key === "ArrowUp") && items.length > 0) {
      event.preventDefault();
      const delta = event.key === "ArrowDown" ? 1 : -1;
      items[(currentIndex + delta + items.length) % items.length]?.focus();
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      (event.key === "Home" ? items[0] : items[items.length - 1])?.focus();
    }
  };

  const runAction = (action: EntityCardAction) => {
    triggerRef.current?.focus();
    setMenuOpen(false);
    action.onClick();
  };

  const openMenu = () => {
    setMenuOpen((open) => !open);
  };

  return (
    <div ref={rowRef} className="entity-list-card account-row relative max-w-full rounded-[var(--ds-radius-lg)]" data-swiped={isSwiped || undefined}>
      <div className="account-row__swipe-shell relative max-w-full overflow-hidden rounded-[var(--ds-radius-lg)]">
      {actions.length > 0 ? (
        <div className="account-row__swipe-actions absolute inset-y-0 right-0 flex" aria-hidden={!isSwiped}>
          {actions.map((action, index) => {
            const ActionIcon = ACTION_ICON[action.kind];
            return (
              <button
                key={action.kind}
                type="button"
                onClick={() => runAction(action)}
                disabled={action.disabled || action.loading}
                aria-label={action.label}
                className={`inline-flex min-h-11 w-20 items-center justify-center disabled:cursor-not-allowed disabled:opacity-50 ${ACTION_CLASS[action.kind]} ${index === actions.length - 1 ? "rounded-r-[var(--ds-radius-lg)]" : ""}`}
                tabIndex={isSwiped ? 0 : -1}
              >
                {action.loading ? <LoadingSpinner /> : <ActionIcon className="h-4 w-4" />}
              </button>
            );
          })}
        </div>
      ) : null}
      <div style={{ transform: `translateX(-${offset}rem)` }} className="account-row__surface relative grid grid-cols-[minmax(0,1fr)_44px] items-center rounded-[var(--ds-radius-lg)] border border-[var(--ds-line)] bg-[var(--ds-surface-raised)] transition-transform duration-300 hover:bg-[var(--ds-line)]">
        <button
          type="button"
          aria-label={accountRow.accessibleLabel}
          onClick={onClick}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerLeave}
          onPointerCancel={onPointerCancel}
          className="account-row__main app-focus-ring grid min-h-[88px] min-w-0 grid-cols-[44px_minmax(0,1fr)_auto_20px] items-center gap-x-3 gap-y-3 rounded-[var(--ds-radius-lg)] p-3 text-left max-[1023px]:grid-rows-[auto_auto] min-[1024px]:grid-cols-[48px_minmax(64px,1fr)_minmax(58px,auto)_minmax(58px,auto)_minmax(92px,auto)_20px] min-[1024px]:gap-x-2 min-[1024px]:p-3"
        >
          <span className={`account-row__icon account-row__icon--${accountRow.iconTone} inline-flex h-11 w-11 items-center justify-center rounded-full text-[var(--ds-on-card)] min-[1024px]:h-12 min-[1024px]:w-12`} aria-hidden="true">
            <Icon className="h-5 w-5" />
          </span>
          <span className="account-row__identity min-w-0 self-center">
            <span className="block truncate text-[17px] font-medium leading-6 text-[var(--ds-ink)] min-[1024px]:text-lg">{title}</span>
            <span className="block truncate text-sm leading-5 text-[var(--ds-ink-muted)]">{accountRow.accountTypeLabel}</span>
            {description ? <span className="account-row__credit-info mt-2 block">{description}</span> : null}
            {progress ? (
              <span className="mt-2 grid max-w-[16rem] grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                <span
                  role="progressbar"
                  aria-label={progress.ariaLabel}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.max(0, Math.min(100, Math.round(progress.percent)))}
                  aria-valuetext={progress.valueText ?? `${Math.round(progress.percent)}%`}
                  className="app-meter min-w-0"
                >
                  <span className={`app-meter__fill block ${progress.percent > 0 ? "min-w-[8px]" : ""}`} style={{ width: `${Math.min(100, Math.max(0, progress.percent))}%`, backgroundColor: PROGRESS_TONE[progress.tone ?? "neutral"] }} />
                </span>
                <span className="whitespace-nowrap text-[13px] text-[var(--ds-ink-muted)]">{progress.label ?? `${Math.round(progress.percent)}%`}</span>
              </span>
            ) : null}
          </span>
          <span className="account-row__desktop-metric hidden whitespace-nowrap text-[15px] text-[var(--ds-ink-muted)] min-[1024px]:block">{accountRow.income}</span>
          <span className="account-row__desktop-metric hidden whitespace-nowrap text-[15px] text-[var(--ds-ink)] min-[1024px]:block">{accountRow.expense}</span>
          <span className={`account-row__balance whitespace-nowrap text-right text-[18px] font-medium tabular-nums ${valueTone === "negative" ? "text-[var(--ds-negative)]" : valueTone === "positive" ? "text-[var(--ds-positive)]" : "text-[var(--ds-ink-muted)]"} min-[1024px]:text-[22px]`}>
            {BalanceIcon ? <BalanceIcon className="mr-1 inline h-4 w-4 min-[1024px]:h-[18px] min-[1024px]:w-[18px]" aria-hidden="true" /> : null}{value}
          </span>
          <FiChevronRight className="account-row__chevron h-5 w-5 text-[var(--ds-ink-muted)] max-[1023px]:hidden" aria-hidden="true" />
          <span className="account-row__mobile-metrics col-span-full grid grid-cols-2 gap-3 border-t border-[var(--ds-line)] pt-3 min-[1024px]:hidden">
            <span>
              <span className="block text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--ds-ink-muted)]">{accountRow.incomeLabel}</span>
              <span className="mt-0.5 block text-[15px] font-medium text-[var(--ds-ink)]">{accountRow.income}</span>
            </span>
            <span>
              <span className="block text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--ds-ink-muted)]">{accountRow.expenseLabel}</span>
              <span className="mt-0.5 block text-[15px] font-medium text-[var(--ds-ink)]">{accountRow.expense}</span>
            </span>
          </span>
        </button>
        <button
          ref={triggerRef}
          type="button"
          className="account-row__menu-trigger app-focus-ring inline-flex h-11 w-11 items-center justify-center rounded-full text-[var(--ds-ink)]"
          aria-label={toggleLabel}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-controls={menuOpen ? menuId : undefined}
          onClick={openMenu}
        >
          <FiMoreHorizontal className="h-5 w-5" aria-hidden="true" />
        </button>
        </div>
      </div>
      {menuOpen && menuPosition ? createPortal(
          <div ref={menuRef} id={menuId} role="menu" aria-label={toggleLabel} tabIndex={-1} onKeyDown={handleMenuKeyDown} className="account-row__menu fixed z-[100] min-w-48 rounded-[var(--ds-radius-md)] border border-[var(--ds-line)] bg-[var(--ds-surface-raised)] p-2 shadow-[var(--ds-shadow-2)]" style={menuPosition}>
            {actions.map((action) => {
              const ActionIcon = ACTION_ICON[action.kind];
              return (
                <button key={action.kind} type="button" role="menuitem" disabled={action.disabled || action.loading} onClick={() => runAction(action)} className={`flex min-h-11 w-full items-center gap-3 rounded-[var(--ds-radius-sm)] px-3 text-left text-[15px] disabled:opacity-50 ${action.kind === "delete" ? "text-[var(--ds-negative)]" : "text-[var(--ds-ink)]"}`}>
                  {action.loading ? <LoadingSpinner /> : <ActionIcon className="h-[18px] w-[18px]" aria-hidden="true" />}
                  {action.label}
                </button>
              );
            })}
          </div>,
          document.body,
        ) : null}
    </div>
  );
};

const StandardEntityCard = ({
  title,
  titleDot,
  titleLead,
  subtitle,
  description,
  descriptionFallback,
  meta,
  value,
  valueTone = "neutral",
  valueCaption,
  progress,
  actions = [],
  isSwiped = false,
  onToggle,
  toggleLabel,
  selection,
  onClick,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerLeave,
  onPointerCancel,
  onContextMenu,
}: EntityCardProps) => {
  const selectionActive = selection?.active ?? false;
  const showActions = actions.length > 0 && !selectionActive;
  const offset = showActions && isSwiped ? actions.length * ACTION_WIDTH_REM : 0;
  const visibleMeta = meta?.filter(Boolean) ?? [];

  return (
    <div className="entity-list-card relative max-w-full touch-pan-y overflow-x-hidden rounded-[var(--ds-radius-lg)]">
      {showActions ? (
        <div className="absolute inset-y-0 right-0 flex">
          {actions.map((action, index) => {
            const Icon = ACTION_ICON[action.kind];
            return (
              <button
                key={action.kind}
                type="button"
                onClick={action.onClick}
                disabled={action.disabled || action.loading}
                aria-busy={action.loading}
                aria-label={action.label}
                className={`inline-flex w-20 items-center justify-center text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${
                  ACTION_CLASS[action.kind]
                } ${index === actions.length - 1 ? "rounded-r-2xl" : ""}`}
              >
                {action.loading ? <LoadingSpinner /> : <Icon className="h-4 w-4" />}
              </button>
            );
          })}
        </div>
      ) : null}

      <button
        type="button"
        onClick={onClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        onPointerCancel={onPointerCancel}
        onContextMenu={onContextMenu}
        style={{ transform: `translateX(-${offset}rem)` }}
        className="entity-list-card__surface group relative block w-full min-w-0 max-w-full text-left"
      >
        <div className="entity-list-card__content flex min-w-0 items-stretch gap-4">
          {selection ? (
            <span
              className={`overflow-hidden transition-[width,opacity] duration-300 ${
                selectionActive ? "w-6 opacity-100" : "w-0 opacity-0"
              }`}
            >
              <span
                className={`mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full border transition-colors duration-200 ${
                  selection.selected
                    ? "border-[var(--app-primary)] bg-[var(--app-primary)] text-[var(--app-on-primary)]"
                    : "border-[var(--app-border)] bg-[var(--app-surface)] text-transparent"
                }`}
              >
                <FiCheck
                  className={`h-3.5 w-3.5 transition-[opacity,transform] duration-200 ${
                    selection.selected ? "scale-100 opacity-100" : "scale-75 opacity-0"
                  }`}
                />
              </span>
            </span>
          ) : null}

          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex min-w-0 items-center gap-2">
              {titleDot ? (
                <span
                  className="inline-flex h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: PROGRESS_TONE[titleDot] }}
                />
              ) : null}
              {titleLead ? <span className="shrink-0">{titleLead}</span> : null}
              <p className="entity-list-card__title truncate text-[var(--app-text)]">{title}</p>
            </div>

            {subtitle ? (
              <p className="entity-list-card__meta mt-1 truncate text-[var(--app-text-secondary)]">
                {subtitle}
              </p>
            ) : null}

            {/* An empty description is a hole, not content: the placeholder drops to the
                muted grey so a row with nothing to say never weighs the same as one that
                describes itself. */}
            {description || descriptionFallback ? (
              <div
                className={`entity-list-card__body mt-1 ${
                  description ? "text-[var(--app-text-secondary)]" : "text-[var(--app-muted-text)]"
                }`}
              >
                {description || descriptionFallback}
              </div>
            ) : null}

            {valueCaption ? (
              <p className="entity-list-card__meta mt-2 max-w-full text-[var(--app-text-secondary)]">
                {valueCaption}
              </p>
            ) : null}

            {visibleMeta.length > 0 ? (
              <div className="entity-list-card__meta mt-auto flex max-w-full flex-wrap items-center gap-x-3 gap-y-0 pt-2 text-[var(--app-text-secondary)] [&>*]:inline-block [&>*]:max-w-full [&>*]:truncate">
                {visibleMeta.map((node, index) => (
                  <span key={index}>{node}</span>
                ))}
              </div>
            ) : null}

            {progress ? (
              <div className="mt-4 grid w-full grid-cols-[minmax(0,1fr)_3.5rem] items-center gap-3">
                <div
                  role="progressbar"
                  aria-label={progress.ariaLabel}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.max(0, Math.min(100, Math.round(progress.percent)))}
                  aria-valuetext={progress.valueText ?? `${Math.round(progress.percent)}%`}
                  className="app-meter min-w-0"
                >
                  <div
                    className="app-meter__fill"
                    style={{
                      width: `${Math.min(100, Math.max(0, progress.percent))}%`,
                      backgroundColor: PROGRESS_TONE[progress.tone ?? "neutral"],
                    }}
                  />
                </div>
                <span className="text-sm font-semibold text-[var(--app-text)] tabular-nums">
                  {progress.label ?? `${Math.round(progress.percent)}%`}
                </span>
              </div>
            ) : null}
          </div>

          <div className="ml-auto flex min-w-0 shrink-0 items-start gap-2">
            <div className="min-w-0 text-right">
              {value ? (
                <p
                  className={`max-w-[38vw] truncate text-base font-semibold tabular-nums ${TONE_CLASS[valueTone]}`}
                >
                  {value}
                </p>
              ) : null}
            </div>

            <span
              role="button"
              tabIndex={selectionActive ? -1 : 0}
              aria-label={toggleLabel}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={onToggle}
              className={`app-focus-ring inline-flex h-11 shrink-0 items-center justify-center overflow-hidden rounded-full text-[color:color-mix(in_srgb,var(--app-text-secondary)_24%,transparent)] transition-[background-color,color,width,opacity] duration-300 group-hover:bg-[var(--app-surface-soft)] group-hover:text-[var(--app-primary)] ${
                selectionActive ? "w-0 opacity-0" : "w-11 opacity-100"
              }`}
            >
              <FiChevronRight
                className={`h-4 w-4 transition-transform duration-300 ${
                  isSwiped ? "rotate-180" : "rotate-0"
                }`}
              />
            </span>
          </div>
        </div>
      </button>
    </div>
  );
};
